import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField } from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { Container, Header, HeaderLeft, Title, Content, BackButton } from '../../../../../common/styles';
import { MainContainer } from '../../../styles';
import { skillsApi } from '../../../../../../services/api';
import { useAlert } from '../../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../../common/LoadingSpinner';
import EditableItemList from '../../../../../MyActivities/MyCV/components/common/EditableItemList';
import CompetenciesDisplay from '../common/CompetenciesDisplay';
import { getCompetenciesStatus } from '../../../../../../utils/competenciesHelper';

const SegmentsCompetencies = () => {
  const { matrixId, versionId, skillId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [skill, setSkill] = useState(null);
  const [segments, setSegments] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    fetchData();
  }, [skillId]);

  const fetchData = async () => {
    try {
      const data = await skillsApi.getSkillTemplate(versionId, skillId);
      setSkill(data);
      
      // Extract segments from mappings
      const extractedSegments = data.mappings?.map(mapping => ({
        id: mapping.segment.id,
        mappingId: mapping.id,
        name: mapping.segment.segment.name,
        description: mapping.segment.segment.description || '',
        originalData: mapping.segment,
        competencies: mapping.competencies || []
      })) || [];
      
      setSegments(extractedSegments);
    } catch (error) {
      showAlert('Failed to fetch skill segments', 'error', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewSegment = () => ({
    id: null,
    mappingId: null,
    name: '',
    description: '',
    originalData: null
  });

  const renderSegmentSummary = (segment) => {
    const status = getCompetenciesStatus(segment.competencies);

    return (
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {segment.name || 'Unnamed Technology'}
        </Typography>
        {segment.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
            {segment.description}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            fontStyle: !status.hasCompetencies || !status.isComplete ? 'italic' : 'normal',
            color: status.color,
            fontSize: '0.75rem',
            mt: 0.5
          }}
        >
          {status.message}
        </Typography>
      </Box>
    );
  };

  const renderSegmentEditForm = (segment, onChange) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          Technology Competencies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure specific competency levels for this technology.
        </Typography>
        
        <CompetenciesDisplay
          competencies={segment?.competencies || []}
          readonly={false}
          onUpdate={fetchData}
          emptyStateTitle="No Competencies Available"
          emptyStateDescription="No competency levels have been defined for this skill yet. Reattach the technology to the skill."
          onEditSubmit={async (competency, description) => {
            await skillsApi.updateSegmentCompetencyDescription(competency.id, description);
          }}
        />
      </Box>
    </Box>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(`/skills-matrix/templates/${matrixId}/versions/${versionId}/configure`)}>
            <FaArrowLeft /> Back to Skills Configuration
          </BackButton>
          <Title>{skill?.skill?.name} | Technology Competencies</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainContainer>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Assigned Technologies
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure competency levels for each technology assigned to this skill. 
              You can define specific competency requirements for different technologies.
            </Typography>
          </Box>

          <EditableItemList
            items={segments}
            setItems={setSegments}
            expandedIndex={expandedIndex}
            setExpandedIndex={setExpandedIndex}
            createNewItem={createNewSegment}
            renderSummary={renderSegmentSummary}
            renderEditForm={renderSegmentEditForm}
            addButtonText="Add Technology"
            emptyState={{
              title: "No Technologies Assigned",
              description: "No technologies have been assigned to this skill yet. Technologies are managed in the Configure Technologies section."
            }}
            itemName="technology"
            isLoading={loading}
            readonly={true}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default SegmentsCompetencies; 